import React, { useState, useEffect, useRef } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import AddressAutocomplete from '../components/clients/AddressAutocomplete';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { DateInput } from '../components/ui/date-input';
import { toast } from 'sonner';
import { calcolaCodiceFiscale } from '../utils/fiscalCode';
import ClientCompletionBanner from '../components/clients/ClientCompletionBanner';
import { Plus, Search, MoreHorizontal, FileText, Mail, Check, X, Download, Send, Trash2, Eye, ArrowUp, ArrowDown, ArrowUpDown, Settings, Server, Loader2, CheckCircle, XCircle, RefreshCw, Copy, UserPlus, Upload, Sparkles, PenTool, IdCard } from 'lucide-react';
import { emptyClient } from '../constants/clientConstants';
import { DelegaGeneraleBadge, FinestraDelegaGenerale } from '../components/DelegaGenerale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Mappa il tipo documento scelto sul document_type richiesto da /api/ocr/extract-client-data
const OCR_DOC_TYPE_MAP = {
  "Carta d'identità elettronica": 'carta_identita',
  "Carta d'identità cartacea": 'carta_identita',
  'Patente': 'patente',
  'Passaporto': 'passaporto',
  'Permesso di soggiorno': 'permesso_soggiorno',
};

// Etichetta breve con cui il documento caricato viene salvato tra i "Documenti anagrafici"
// del cliente (stessa convenzione già usata in Dimissioni.js/Ospitalita.js).
const DOC_LABEL_MAP = {
  "Carta d'identità elettronica": 'CIE',
  "Carta d'identità cartacea": 'CIE',
  'Patente': 'PAT',
  'Passaporto': 'PASS',
  'Permesso di soggiorno': 'PSE',
};

// "Carta d'identità cartacea" non più selezionabile nei caricamenti (resta nelle mappe solo per
// le richieste già salvate con quel tipo).
const NEW_CLIENT_DOC_TIPO_OPTIONS = Object.keys(OCR_DOC_TYPE_MAP).filter(o => o !== "Carta d'identità cartacea");

const CanoneRai = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [yearFilter, setYearFilter] = useState('all');
  const [selectedClientId, setSelectedClientId] = useState('');
  // Default: anno prossimo (le richieste di esenzione si fanno per l'anno a venire)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear() + 1);
  const [notes, setNotes] = useState('');
  // Documento d'identità del cliente, per la Delega trasmissione (Agenzia delle Entrate)
  const [docTipo, setDocTipo] = useState('');
  const [docNumero, setDocNumero] = useState('');
  const [docEnte, setDocEnte] = useState('');
  const [sendingPec, setSendingPec] = useState(null);
  const [sendPecConfirm, setSendPecConfirm] = useState(null); // record in attesa di conferma invio
  const [sendCopyToClient, setSendCopyToClient] = useState(false);
  const [sendingRicevuteEmail, setSendingRicevuteEmail] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // ============ NUOVO CLIENTE INLINE (estrazione OCR documento) =============
  const [newClientDialogOpen, setNewClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState(emptyClient);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClientDocTipo, setNewClientDocTipo] = useState("Carta d'identità elettronica");
  const [extractingNewClientDoc, setExtractingNewClientDoc] = useState(false);
  // Documento caricato mentre il cliente non esiste ancora: va allegato in anagrafica solo
  // DOPO la creazione del cliente (vedi saveNewClient).
  const [pendingNewClientDoc, setPendingNewClientDoc] = useState(null); // { file, label, expiryDate }
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  // Upload documento identità per il cliente GIÀ selezionato in "Nuova Richiesta" (estrae
  // numero/ente e salva il documento in anagrafica, stessa convenzione del box "Nuovo cliente").
  const mainDocInputRef = useRef(null);
  const [extractingMainDoc, setExtractingMainDoc] = useState(false);
  // Upload Delega scansionata (firmata dal cliente) → ritaglio automatico della firma
  const delegaFirmataInputRef = useRef(null);
  const [delegaFirmataTargetId, setDelegaFirmataTargetId] = useState(null);
  const [uploadingDelegaFirmata, setUploadingDelegaFirmata] = useState(null);

  // PEC Config state
  const [pecDialogOpen, setPecDialogOpen] = useState(false);
  const [pecConfig, setPecConfig] = useState({
    pec_email: '',
    pec_password: '',
    smtp_server: '',
    smtp_port: 465,
    imap_server: '',
    imap_port: 993,
    default_recipient: 'cp22.canonetv@postacertificata.rai.it',
  });
  const [pecConfigLoaded, setPecConfigLoaded] = useState(null);  // info ricevute dal GET
  const [pecSaving, setPecSaving] = useState(false);
  const [pecTesting, setPecTesting] = useState(false);
  const [pecTestRecipient, setPecTestRecipient] = useState('');
  const [pecTestResult, setPecTestResult] = useState(null);

  // Ricevute PEC (IMAP + stampa) — stesso meccanismo di Ospitalità
  const [fetchingRicevute, setFetchingRicevute] = useState(null);
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: '', title: '' });
  // Anteprima del modulo da allegare alla PEC, mostrata nella conferma di invio
  const [pecPreview, setPecPreview] = useState({ url: '', loading: false, error: '' });

  useEffect(() => {
    if (!sendPecConfirm) return undefined;
    let annullato = false;
    let blobUrl = '';
    setPecPreview({ url: '', loading: true, error: '' });
    axios.get(`${API}/canone-rai/${sendPecConfirm.id}/generate-pdf`, { responseType: 'blob' })
      .then((res) => {
        if (annullato) return;
        blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPecPreview({ url: blobUrl, loading: false, error: '' });
      })
      .catch(() => { if (!annullato) setPecPreview({ url: '', loading: false, error: 'Impossibile generare l\'anteprima del modulo' }); });
    return () => {
      annullato = true;
      if (blobUrl) { try { window.URL.revokeObjectURL(blobUrl); } catch (_e) {} }
    };
  }, [sendPecConfirm]);

  const fetchPecConfig = async () => {
    try {
      const res = await axios.get(`${API}/canone-rai-pec-config`);
      setPecConfigLoaded(res.data);
      setPecConfig(c => ({
        ...c,
        pec_email: res.data.pec_email || '',
        smtp_server: res.data.smtp_server || '',
        smtp_port: res.data.smtp_port || 465,
        imap_server: res.data.imap_server || '',
        imap_port: res.data.imap_port || 993,
        default_recipient: res.data.default_recipient || 'cp22.canonetv@postacertificata.rai.it',
        pec_password: '',  // password mai mostrata, vuoto = "non cambiare"
      }));
      // suggerisce destinatario di test = email PEC stessa
      if (!pecTestRecipient && res.data.pec_email) setPecTestRecipient(res.data.pec_email);
    } catch (error) {
      toast.error('Errore nel caricamento configurazione PEC');
    }
  };

  const savePecConfig = async () => {
    if (!pecConfig.pec_email || !pecConfig.smtp_server) {
      toast.error('Email PEC e Server SMTP sono obbligatori');
      return;
    }
    if (!pecConfigLoaded?.password_set && !pecConfig.pec_password) {
      toast.error('Password PEC obbligatoria al primo salvataggio');
      return;
    }
    setPecSaving(true);
    try {
      const payload = {
        pec_email: pecConfig.pec_email.trim(),
        smtp_server: pecConfig.smtp_server.trim(),
        smtp_port: parseInt(pecConfig.smtp_port),
        imap_server: (pecConfig.imap_server || pecConfig.smtp_server).trim(),
        imap_port: parseInt(pecConfig.imap_port) || 993,
        default_recipient: pecConfig.default_recipient.trim(),
      };
      if (pecConfig.pec_password) payload.pec_password = pecConfig.pec_password;
      await axios.put(`${API}/canone-rai-pec-config`, payload);
      toast.success('Configurazione PEC salvata');
      fetchPecConfig();
      // pulisci campo password dopo salvataggio
      setPecConfig(c => ({ ...c, pec_password: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setPecSaving(false);
    }
  };

  const testPecConfig = async () => {
    if (!pecTestRecipient) {
      toast.error('Inserisci un destinatario di test');
      return;
    }
    setPecTesting(true);
    setPecTestResult(null);
    try {
      const payload = {
        test_recipient: pecTestRecipient.trim(),
      };
      // se l'utente ha cambiato qualcosa senza ancora salvare, testa coi valori nuovi
      if (pecConfig.pec_email) payload.pec_email = pecConfig.pec_email.trim();
      if (pecConfig.pec_password) payload.pec_password = pecConfig.pec_password;
      if (pecConfig.smtp_server) payload.smtp_server = pecConfig.smtp_server.trim();
      if (pecConfig.smtp_port) payload.smtp_port = parseInt(pecConfig.smtp_port);
      const res = await axios.post(`${API}/canone-rai-pec-config/test`, payload);
      setPecTestResult({ ok: true, message: res.data.message });
      toast.success('Email di test inviata');
    } catch (error) {
      const err = error.response?.data?.detail || 'Errore nel test';
      setPecTestResult({ ok: false, message: err });
      toast.error(err);
    } finally {
      setPecTesting(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchClients();
  }, [yearFilter]);

  const fetchRecords = async () => {
    try {
      let url = `${API}/canone-rai`;
      if (yearFilter && yearFilter !== 'all') {
        url += `?year=${yearFilter}`;
      }
      const response = await axios.get(url);
      setRecords(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei record');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleClientSearch = (term) => {
    setClientSearchTerm(term);
    setShowClientDropdown(true);
    if (!term.trim()) {
      setFilteredClients(clients);
      return;
    }
    const filtered = clients.filter(c =>
      `${c.last_name} ${c.first_name}`.toLowerCase().includes(term.toLowerCase()) ||
      c.fiscal_code?.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredClients(filtered);
  };

  // ============ NUOVO CLIENTE INLINE (estrazione OCR documento) =============
  const openNewClient = () => {
    setNewClient(emptyClient);
    setNewClientDocTipo("Carta d'identità elettronica");
    setPendingNewClientDoc(null);
    setNewClientDialogOpen(true);
  };

  // Pulsante "Calcola CF" del form nuovo cliente (stesso di Anagrafica Clienti): calcola il CF
  // dai dati anagrafici e avvisa se appartiene già a un cliente registrato.
  const handleCalcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClient);
      setNewClient(prev => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) {
        toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      } else {
        toast.success('Codice Fiscale calcolato');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Prova ad abbinare un cliente esistente in anagrafica tramite codice fiscale estratto
  const matchClientByCf = (cf) => {
    if (!cf) return null;
    return clients.find(c => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null;
  };

  // Allega il file caricato ai "Documenti anagrafici" del cliente, rinominato come
  // "SIGLA COGNOME NOME" (es. "CIE ROSSI MARIO") — stessa convenzione di Dimissioni/Ospitalità.
  // Se esiste già un documento dello stesso tipo (stessa sigla) in anagrafica, lo sostituisce
  // invece di aggiungerne un altro — la registrazione di un tipo documento va fatta una sola volta.
  const uploadClientDocument = async (clientId, file, shortLabel, lastName, firstName, expiryDate) => {
    try {
      const label = `${shortLabel} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        const existing = (clientRes.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${shortLabel} `)
        );
        if (existing) {
          await axios.delete(`${API}/clients/${clientId}/documents/${existing.id}`);
        }
      } catch (_) {
        // non bloccare l'upload se il controllo duplicati fallisce
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati estratti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  // Estrae numero/ente dal documento del cliente GIÀ selezionato (form "Nuova Richiesta") e lo
  // salva direttamente in anagrafica — stessa logica OCR usata per "Nuovo cliente", ma qui il
  // cliente esiste già e non va toccata l'anagrafica oltre al documento allegato.
  const handleExtractMainDoc = async (file) => {
    if (!file) return;
    if (!selectedClientId) {
      toast.error('Seleziona prima un cliente');
      return;
    }
    if (!docTipo) {
      toast.error('Seleziona prima il tipo di documento');
      return;
    }
    const ocrType = OCR_DOC_TYPE_MAP[docTipo];
    const docLabel = DOC_LABEL_MAP[docTipo] || ocrType.toUpperCase();
    setExtractingMainDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const numero = (d.document_number || d.license_number || '').toUpperCase();
      if (numero) setDocNumero(numero);
      if (d.issuing_authority) setDocEnte(d.issuing_authority.toUpperCase());
      const client = clients.find(c => c.id === selectedClientId);
      await uploadClientDocument(selectedClientId, file, docLabel, client?.last_name, client?.first_name, d.expiry_date);
      toast.success('Dati estratti e documento salvato in anagrafica');
    } catch (error) {
      toast.error(`Errore estrazione documento: ${error.response?.data?.detail || 'errore'}`);
    } finally {
      setExtractingMainDoc(false);
    }
  };

  const handleExtractNewClientDoc = async (file) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[newClientDocTipo];
    const docLabel = DOC_LABEL_MAP[newClientDocTipo] || ocrType.toUpperCase();
    setExtractingNewClientDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      if (!c.fiscal_code && !c.first_name) {
        toast.error('Nessun dato riconosciuto nel documento — verifica di aver scelto il tipo documento corretto');
        return;
      }

      // Documento usato anche per la Delega trasmissione (vedi form principale)
      const numero = d.document_number || d.license_number || '';
      setDocTipo(newClientDocTipo);
      setDocNumero(numero);
      setDocEnte(d.issuing_authority || '');

      const up = (v) => (v || '').toUpperCase();
      let merged = {
        ...newClient,
        first_name: up(c.first_name) || newClient.first_name,
        last_name: up(c.last_name) || newClient.last_name,
        fiscal_code: c.fiscal_code || newClient.fiscal_code,
        gender: c.gender || newClient.gender,
        birth_date: c.birth_date || newClient.birth_date,
        birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClient.birth_abroad,
        birth_comune: up(c.birth_comune) || (c.birth_abroad ? '' : newClient.birth_comune),
        birth_provincia: c.birth_provincia || newClient.birth_provincia,
        birth_country: up(c.birth_country) || newClient.birth_country,
        address: up(c.address) || newClient.address,
        address_number: c.address_number || newClient.address_number,
        city: up(c.city) || newClient.city,
        city_provincia: c.city_provincia || newClient.city_provincia,
      };

      // CAP non riportato sul documento (le CIE italiane non lo stampano nell'indirizzo) —
      // se comune e provincia di residenza sono noti e individuano un solo CAP possibile,
      // lo compiliamo in automatico invece di lasciarlo vuoto.
      if (!merged.postal_code && merged.city && merged.city_provincia) {
        try {
          const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
          if (capRes.data.single && capRes.data.auto_cap) {
            merged = { ...merged, postal_code: capRes.data.auto_cap };
          }
        } catch (e) {
          // il CAP è un aiuto in più: se il lookup fallisce non deve bloccare il resto del flusso
        }
      }

      // Il documento (es. Passaporto) spesso non riporta il CF italiano — se ora abbiamo
      // tutti i dati per calcolarlo, lo calcoliamo in automatico invece di lasciarlo vuoto.
      const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
        ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
      if (canCalcCf) {
        try {
          const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
            surname: merged.last_name,
            name: merged.first_name,
            birth_date: merged.birth_date,
            gender: merged.gender,
            birth_abroad: merged.birth_abroad,
            birth_comune: merged.birth_comune,
            birth_provincia: merged.birth_provincia,
            birth_country: merged.birth_country,
          });
          if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
        } catch (e) {
          // il calcolo CF è un aiuto in più: se fallisce non deve bloccare il resto del flusso
        }
      }

      // Il CF appena calcolato (o quello letto dal documento) potrebbe corrispondere a un
      // cliente già esistente — evita di creare un duplicato.
      const match = matchClientByCf(merged.fiscal_code);
      if (match) {
        setSelectedClientId(match.id);
        setClientSearchTerm(`${match.last_name} ${match.first_name}`);
        setNewClient(merged);
        await uploadClientDocument(match.id, file, docLabel, match.last_name, match.first_name, d.expiry_date);
        toast.success(`Cliente esistente abbinato: ${match.first_name} ${match.last_name} — documento allegato in anagrafica. Controlla i dati e chiudi quando hai verificato.`);
        // Il dialog resta aperto apposta: l'operatore deve poter controllare i dati estratti
        // prima di chiuderlo (bottone "Annulla"), non si chiude più da solo.
      } else {
        setPendingNewClientDoc({ file, label: docLabel, expiryDate: d.expiry_date || '' });
        setNewClient(merged);
        toast.success('Dati estratti — rivedi e completa la creazione (il documento si allega automaticamente)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal documento');
    } finally {
      setExtractingNewClientDoc(false);
    }
  };

  const saveNewClient = async () => {
    if (!newClient.last_name || !newClient.first_name) {
      toast.error('Cognome e Nome obbligatori');
      return;
    }
    if (!newClient.birth_abroad && (!newClient.birth_comune || !newClient.birth_provincia)) {
      toast.error('Compila Comune e Provincia di nascita (o attiva "Nato/a all\'estero")');
      return;
    }
    if (newClient.birth_abroad && !newClient.birth_country) {
      toast.error('Seleziona la Nazione di nascita');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await axios.post(`${API}/clients`, newClient);
      const created = res.data;
      if (pendingNewClientDoc) {
        await uploadClientDocument(created.id, pendingNewClientDoc.file, pendingNewClientDoc.label, newClient.last_name, newClient.first_name, pendingNewClientDoc.expiryDate);
        setPendingNewClientDoc(null);
      }
      await fetchClients();
      setSelectedClientId(created.id);
      setClientSearchTerm(`${newClient.last_name} ${newClient.first_name}`);
      toast.success('Cliente creato');
      setNewClientDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione cliente');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedClientId) {
      toast.error('Seleziona un cliente');
      return;
    }

    try {
      await axios.post(`${API}/canone-rai`, {
        client_id: selectedClientId,
        year: selectedYear,
        notes: notes,
        doc_tipo: docTipo,
        doc_numero: docNumero,
        doc_ente: docEnte,
      });
      toast.success('Record creato con successo');
      setDialogOpen(false);
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const resetForm = () => {
    setSelectedClientId('');
    setSelectedYear(new Date().getFullYear() + 1);
    setNotes('');
    setDocTipo('');
    setDocNumero('');
    setDocEnte('');
    setClientSearchTerm('');
    setFilteredClients(clients);
    setShowClientDropdown(false);
  };

  // Clona una richiesta esistente: apre "Nuova Richiesta" con lo stesso cliente e l'anno
  // successivo (rinnovo l'anno dopo), oppure lo stesso anno se la PEC non è stata consegnata
  // e la richiesta va rimandata.
  const handleClone = (record, stessoAnno = false) => {
    setSelectedClientId(record.client_id);
    setSelectedYear((record.year || new Date().getFullYear()) + (stessoAnno ? 0 : 1));
    if (stessoAnno) toast.info('Copia della richiesta non consegnata: salva e rimanda la PEC');
    setNotes(record.notes || '');
    setDocTipo(record.doc_tipo || '');
    setDocNumero(record.doc_numero || '');
    setDocEnte(record.doc_ente || '');
    setClientSearchTerm(record.client_name || '');
    setDialogOpen(true);
  };

  const handleUpdatePayment = async (recordId, paymentDone) => {
    try {
      await axios.put(`${API}/canone-rai/${recordId}`, { payment_done: paymentDone });
      toast.success('Stato pagamento aggiornato');
      fetchRecords();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleGeneratePdf = async (recordId) => {
    try {
      const response = await axios.get(`${API}/canone-rai/${recordId}/generate-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'CANONE_RAI.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generato e scaricato');
      fetchRecords();
    } catch (error) {
      toast.error('Errore nella generazione del PDF');
    }
  };

  const handleSendPec = async (record, sendCopy = false) => {
    const recordId = record.id;
    setSendingPec(recordId);
    try {
      const response = await axios.post(`${API}/canone-rai/${recordId}/send-pec`, null, {
        params: { send_copy_to_client: sendCopy }
      });
      toast.success(response.data.message || 'PEC inviata con successo');
      if (sendCopy) {
        if (response.data.copy_sent_to_client) {
          toast.success('Copia email inviata al cliente');
        } else if (response.data.copy_error) {
          toast.error(`Copia email al cliente non inviata: ${response.data.copy_error}`);
        }
      }
      fetchRecords();
      // Niente anteprima automatica dopo l'invio: il modulo è già stato controllato nella
      // conferma "Invia PEC". Resta riapribile dal menu della riga ("PDF inviato").
      // Auto-aggiornamento silenzioso: 6s dopo l'invio, controlla le ricevute PEC
      // (solo le ultime 10 richieste ancora scoperte). Se le trova, i badge si
      // aggiornano da soli e quel record non viene più ricontrollato.
      setTimeout(() => {
        axios.post(`${API}/canone-rai/fetch-ricevute-all?limit=10`)
          .then(() => fetchRecords())
          .catch(() => {});
      }, 6000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della PEC');
    } finally {
      setSendingPec(null);
    }
  };

  const handleSendRicevuteEmail = async (record) => {
    setSendingRicevuteEmail(record.id);
    try {
      const response = await axios.post(`${API}/canone-rai/${record.id}/send-ricevute-email`);
      toast.success(response.data.message || 'Ricevute inviate al cliente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio delle ricevute al cliente');
    } finally {
      setSendingRicevuteEmail(null);
    }
  };

  // ============ RICEVUTE PEC (IMAP + STAMPA) ==============
  const fetchRicevute = async (record) => {
    if (!record.pec_sent) {
      toast.error('Devi prima inviare la PEC');
      return;
    }
    setFetchingRicevute(record.id);
    try {
      toast.loading('Scarico ricevute PEC dalla casella IMAP...', { id: `fetch-${record.id}` });
      const res = await axios.post(`${API}/canone-rai/${record.id}/fetch-ricevute`);
      const found = res.data.found || [];
      if (found.length === 0) {
        toast.info('Nessuna nuova ricevuta trovata (potrebbe non essere ancora arrivata)', { id: `fetch-${record.id}` });
      } else {
        toast.success(`Trovate: ${found.join(' + ')}`, { id: `fetch-${record.id}` });
      }
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore IMAP', { id: `fetch-${record.id}` });
    } finally {
      setFetchingRicevute(null);
    }
  };

  // Anteprima del PDF (modulo Canone RAI compilato) — lo stesso allegato della
  // PEC. Richiamabile sia subito dopo l'invio, sia in qualsiasi momento dopo
  // (rigenera il PDF al volo con i dati correnti del cliente).
  const previewSentPdf = async (record) => {
    try {
      const pdfRes = await axios.get(`${API}/canone-rai/${record.id}/generate-pdf`, { responseType: 'blob' });
      const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewDialog({ open: true, url: blobUrl, title: `PDF inviato — ${record.client_name || record.id.slice(0, 8)}` });
    } catch (previewError) {
      console.error('Anteprima PDF non disponibile:', previewError);
      toast.error('Impossibile caricare l\'anteprima del PDF');
    }
  };

  // Anteprima di un documento già salvato in anagrafica cliente (firma estratta dalla Delega
  // firmata, documento d'identità) — endpoint generico già usato altrove nel CRM.
  const previewClientDocument = async (docId, title) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}`, { responseType: 'blob' });
      const contentType = res.headers['content-type'] || 'application/pdf';
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewDialog({ open: true, url: blobUrl, title });
    } catch (error) {
      toast.error('Documento non disponibile');
    }
  };

  // Anteprima della scansione della Delega firmata così come caricata dall'operatore (non il
  // solo ritaglio della firma) — salvata sulla pratica da upload-delega-firmata.
  const previewDelegaFirmataScan = async (record) => {
    try {
      const res = await axios.get(`${API}/canone-rai/${record.id}/delega-firmata`, { responseType: 'blob' });
      const contentType = res.headers['content-type'] || 'application/pdf';
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewDialog({ open: true, url: blobUrl, title: `Delega firmata (scansione) — ${record.client_name || record.id.slice(0, 8)}` });
    } catch (error) {
      toast.error('Delega firmata non disponibile');
    }
  };

  // Delega trasmissione (Agenzia delle Entrate) — generata al volo dai dati correnti della
  // pratica/cliente, non salvata su disco.
  const previewDelegaPdf = async (record) => {
    try {
      const pdfRes = await axios.get(`${API}/canone-rai/${record.id}/delega-pdf`, { responseType: 'blob' });
      const blob = new Blob([pdfRes.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      setPreviewDialog({ open: true, url: blobUrl, title: `Delega — ${record.client_name || record.id.slice(0, 8)}` });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile generare la Delega');
    }
  };

  // Apre il file picker per caricare la Delega scansionata (firmata a mano dal cliente) di
  // un record specifico — il click reale avviene sull'input nascosto qui sotto.
  const triggerUploadDelegaFirmata = (record) => {
    setDelegaFirmataTargetId(record.id);
    delegaFirmataInputRef.current?.click();
  };

  // Delega generale (sostituisce la "Delega trasmissione" RAI): dopo il caricamento della firmata la
  // firma è in anagrafica → anteprima subito del modulo RAI con la firma applicata, come prima
  const [delegaGenerale, setDelegaGenerale] = useState(null);   // record della pratica
  const dopoDelegaFirmata = async (record) => {
    fetchRecords();
    try {
      const pdfRes = await axios.get(`${API}/canone-rai/${record.id}/generate-pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      setPreviewDialog({ open: true, url: blobUrl, title: `Anteprima modulo con firma — ${record.client_name || record.id.slice(0, 8)}` });
    } catch (_) { /* l'anteprima è solo un aiuto */ }
  };

  const handleUploadDelegaFirmata = async (file) => {
    if (!file || !delegaFirmataTargetId) return;
    setUploadingDelegaFirmata(delegaFirmataTargetId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/canone-rai/${delegaFirmataTargetId}/upload-delega-firmata`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.riquadro_trovato) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message, { duration: 8000 });
      }
      fetchRecords();
      // Anteprima subito del modulo RAI con la firma appena estratta, per controllare dove e
      // come è stata applicata prima di inviare la PEC.
      const record = records.find(r => r.id === delegaFirmataTargetId) || { id: delegaFirmataTargetId };
      const pdfRes = await axios.get(`${API}/canone-rai/${delegaFirmataTargetId}/generate-pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      setPreviewDialog({ open: true, url: blobUrl, title: `Anteprima modulo con firma — ${record.client_name || record.id.slice(0, 8)}` });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile estrarre la firma dalla scansione');
    } finally {
      setUploadingDelegaFirmata(null);
      setDelegaFirmataTargetId(null);
    }
  };

  const downloadRicevuta = async (record, tipo, mode = 'clean') => {
    try {
      const url = `${API}/canone-rai/${record.id}/ricevuta/${tipo}/${mode}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const tipoLabel = tipo === 'accettazione' ? 'Accettazione' : 'Consegna';
      const title = `Ricevuta ${tipoLabel} — ${record.client_name || record.id.slice(0, 8)}`;
      setPreviewDialog({ open: true, url: blobUrl, title });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ricevuta non ancora disponibile');
    }
  };

  // Scarica PDF UNIFICATO Accettazione+Consegna in un unico documento multi-pagina
  const downloadRicevuteCombinate = async (record) => {
    try {
      const url = `${API}/canone-rai/${record.id}/ricevuta/combined/clean`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const title = `Ricevute PEC — ${record.client_name || record.id.slice(0, 8)}`;
      setPreviewDialog({ open: true, url: blobUrl, title });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ricevute non ancora disponibili');
    }
  };

  const closePreview = () => {
    if (previewDialog.url) {
      try { window.URL.revokeObjectURL(previewDialog.url); } catch (_e) {}
    }
    setPreviewDialog({ open: false, url: '', title: '' });
  };

  const fetchAllRicevute = async () => {
    try {
      toast.loading('Controllo ricevute PEC per tutte le richieste...', { id: 'fetch-all-rai' });
      const res = await axios.post(`${API}/canone-rai/fetch-ricevute-all`);
      toast.success(`Controllate ${res.data.processed} richieste, aggiornate ${res.data.updated}`, { id: 'fetch-all-rai' });
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore IMAP', { id: 'fetch-all-rai' });
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Eliminare questo record?')) return;
    try {
      await axios.delete(`${API}/canone-rai/${recordId}`);
      toast.success('Record eliminato');
      fetchRecords();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  // Sort function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />;
  };

  // Filter and sort records
  const filteredRecords = records
    .filter(r => 
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.client_fiscal_code?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal = a[key];
      let bVal = b[key];
      
      // Handle date sorting
      if (key === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }
      // Handle boolean sorting (payment_done, pec_sent)
      if (typeof aVal === 'boolean') {
        aVal = aVal ? 1 : 0;
        bVal = bVal ? 1 : 0;
      }
      // Handle year sorting
      if (key === 'year') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination
  const totalItems = filteredRecords.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, yearFilter]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="canone-rai-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Canone RAI - Esenzioni
          </h1>
          <p className="text-slate-600 mt-1">Gestione richieste esenzione Canone RAI</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchAllRicevute}
            data-testid="fetch-all-ricevute-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />Scarica ricevute PEC
          </Button>
          <Button
            variant="outline"
            onClick={() => { fetchPecConfig(); setPecDialogOpen(true); }}
            data-testid="configure-pec-btn"
          >
            <Settings className="w-4 h-4 mr-2" />Configura PEC
          </Button>
          <Button 
            className="bg-slate-900 hover:bg-slate-800 text-white" 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            data-testid="new-canone-rai-btn"
          >
            <Plus className="w-4 h-4 mr-2" />Nuova Richiesta
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome cliente o codice fiscale..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-canone-rai"
              />
            </div>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-40" data-testid="year-filter">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli anni</SelectItem>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessun record trovato</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Codice Fiscale</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('year')}
                    >
                      <div className="flex items-center">
                        Anno
                        {getSortIcon('year')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center">
                        Data Inserimento
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('payment_done')}
                    >
                      <div className="flex items-center">
                        Pagamento
                        {getSortIcon('payment_done')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50 select-none"
                      onClick={() => handleSort('pec_sent')}
                    >
                      <div className="flex items-center">
                        PEC Inviata
                        {getSortIcon('pec_sent')}
                      </div>
                    </TableHead>
                    <TableHead>PDF</TableHead>
                    <TableHead>Ricevute al cliente</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRecords.map((record) => (
                    <TableRow key={record.id} data-testid={`canone-rai-row-${record.id}`}>
                      <TableCell className="font-medium">
                        {record.client_name}
                        <div className="mt-1"><DelegaGeneraleBadge clientId={record.client_id} clientName={record.client_name} onFirmata={() => dopoDelegaFirmata(record)} /></div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{record.client_fiscal_code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{record.year}</Badge>
                      </TableCell>
                      <TableCell>{record.insertion_date ? new Date(record.insertion_date).toLocaleDateString('it-IT') : '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdatePayment(record.id, !record.payment_done)}
                          className={record.payment_done ? 'text-green-600' : 'text-slate-400'}
                        >
                          {record.payment_done ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                          <span className="ml-1">{record.payment_done ? 'Sì' : 'No'}</span>
                        </Button>
                      </TableCell>
                      <TableCell>
                        {record.pec_sent ? (
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-green-100 text-green-700 w-fit">
                              <Mail className="w-3 h-3 mr-1" />
                              Inviata
                            </Badge>
                            <div className="flex gap-1 flex-wrap items-center">
                              {record.ricevuta_accettazione ? (
                                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] px-1.5 py-0" title={`Accettata: ${record.ricevuta_accettazione.date || ''}${record.ricevuta_accettazione.identificativo ? '\nID: ' + record.ricevuta_accettazione.identificativo : ''}`}>
                                  <Check className="w-2.5 h-2.5 mr-0.5" /> Accett.
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Accett. attesa</Badge>
                              )}
                              {(record.ricevuta_mancata_consegna || record.ricevuta_non_accettazione) && !record.ricevuta_consegna ? (
                                <>
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] px-1.5 py-0" title={record.ricevuta_mancata_consegna ? `Mancata consegna: ${record.ricevuta_mancata_consegna.date || ''}${record.ricevuta_mancata_consegna.identificativo ? '\nID: ' + record.ricevuta_mancata_consegna.identificativo : ''}` : `Non accettata: ${record.ricevuta_non_accettazione.date || ''}`}>
                                    <X className="w-2.5 h-2.5 mr-0.5" /> {record.ricevuta_mancata_consegna ? 'Mancata cons.' : 'Non accettata'}
                                  </Badge>
                                  {/* la PEC non è arrivata: si rifà la stessa pratica (nuovo record) e si rimanda */}
                                  <button
                                    onClick={() => handleClone(record, true)}
                                    className="ml-1 px-1.5 py-0 text-[10px] rounded border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center"
                                    title="Non consegnata: crea una copia della stessa pratica da rimandare"
                                  ><Copy className="w-2.5 h-2.5 mr-0.5" /> Clona e rimanda</button>
                                </>
                              ) : record.ricevuta_consegna ? (
                                <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 text-[10px] px-1.5 py-0" title={`Consegnata: ${record.ricevuta_consegna.date || ''}${record.ricevuta_consegna.identificativo ? '\nID: ' + record.ricevuta_consegna.identificativo : ''}`}>
                                  <Check className="w-2.5 h-2.5 mr-0.5" /> Cons.
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Cons. attesa</Badge>
                              )}
                              {(record.ricevuta_accettazione || record.ricevuta_consegna || record.ricevuta_mancata_consegna) && (
                                <button
                                  onClick={() => downloadRicevuteCombinate(record)}
                                  className="ml-1 p-0.5 hover:bg-indigo-50 rounded text-indigo-700 border border-indigo-200"
                                  title="Anteprima ricevute PEC (PDF unico)"
                                  data-testid={`preview-ricevute-${record.id}`}
                                ><FileText className="w-3 h-3" /></button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">
                            Non inviata
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {record.pdf_generated ? (
                            <Badge className="bg-blue-100 text-blue-700">
                              <FileText className="w-3 h-3 mr-1" />
                              Generato
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">Da generare</Badge>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-0.5 hover:bg-indigo-50 rounded text-indigo-700 border border-indigo-200"
                                title="Anteprime e Delega"
                                data-testid={`preview-menu-${record.id}`}
                              ><Eye className="w-3 h-3" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem onClick={() => previewSentPdf(record)} disabled={!record.pdf_generated}>
                                <FileText className="w-4 h-4 mr-2" />Dichiarazione
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => previewClientDocument(record.firma_doc_id, `Firma — ${record.client_name || record.id.slice(0, 8)}`)}
                                disabled={!record.firma_doc_id}
                              >
                                <PenTool className="w-4 h-4 mr-2" />Firma
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => previewClientDocument(record.documento_doc_id, `Documento — ${record.client_name || record.id.slice(0, 8)}`)}
                                disabled={!record.documento_doc_id}
                              >
                                <IdCard className="w-4 h-4 mr-2" />Documento d'identità
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => previewDelegaFirmataScan(record)}
                                disabled={!record.delega_firmata_filename}
                              >
                                <Upload className="w-4 h-4 mr-2" />Delega firmata caricata
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {record.pec_sent && (
                            record.pec_pdf_firma_applicata === true ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0" title="Il modulo RAI allegato alla PEC è stato generato CON la firma">
                                <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Firma sì
                              </Badge>
                            ) : record.pec_pdf_firma_applicata === false ? (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] px-1.5 py-0" title="La PEC è stata inviata con il modulo RAI SENZA firma (firma cliente mancante al momento dell'invio)">
                                <XCircle className="w-2.5 h-2.5 mr-0.5" /> Firma no
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400" title="Invio precedente al tracciamento della firma: stato non disponibile">
                                Firma n/d
                              </Badge>
                            )
                          )}
                          {record.pec_sent && (
                            record.pec_doc_identita_allegato === true ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0" title="Il documento d'identità del cliente è stato allegato alla PEC">
                                <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Doc. ID sì
                              </Badge>
                            ) : record.pec_doc_identita_allegato === false ? (
                              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] px-1.5 py-0" title="La PEC è stata inviata SENZA documento d'identità allegato (nessun documento idoneo trovato tra quelli del cliente)">
                                <XCircle className="w-2.5 h-2.5 mr-0.5" /> Doc. ID no
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400" title="Invio precedente al tracciamento dell'allegato: stato non disponibile">
                                Doc. ID n/d
                              </Badge>
                            )
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(record.ricevuta_accettazione || record.ricevuta_consegna) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendRicevuteEmail(record)}
                            disabled={sendingRicevuteEmail === record.id}
                            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                            data-testid={`send-ricevute-email-${record.id}`}
                          >
                            {sendingRicevuteEmail === record.id
                              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                              : <Mail className="w-4 h-4 mr-1.5" />}
                            Invia al cliente
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleGeneratePdf(record.id)}>
                              <Download className="w-4 h-4 mr-2" />Genera PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDelegaGenerale(record)}>
                              <FileText className="w-4 h-4 mr-2" />Delega generale (stampa / carica firmata)
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => triggerUploadDelegaFirmata(record)}
                              disabled={uploadingDelegaFirmata === record.id}
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              {uploadingDelegaFirmata === record.id ? 'Caricamento...' : 'Carica Delega firmata (estrae firma)'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleClone(record)}>
                              <Copy className="w-4 h-4 mr-2" />Clona (stesso cliente, anno +1)
                            </DropdownMenuItem>
                            {(record.ricevuta_mancata_consegna || record.ricevuta_non_accettazione) && !record.ricevuta_consegna && (
                              <DropdownMenuItem onClick={() => handleClone(record, true)} className="text-red-600">
                                <Copy className="w-4 h-4 mr-2" />Clona e rimanda (non consegnata, stesso anno)
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => { setSendCopyToClient(false); setSendPecConfirm(record); }}
                              disabled={sendingPec === record.id}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {sendingPec === record.id ? 'Invio in corso...' : 'Invia PEC'}
                            </DropdownMenuItem>
                            {record.pec_sent && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => fetchRicevute(record)}
                                  disabled={fetchingRicevute === record.id}
                                  data-testid={`fetch-ricevute-${record.id}`}
                                >
                                  {fetchingRicevute === record.id
                                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    : <Mail className="w-4 h-4 mr-2" />}
                                  {fetchingRicevute === record.id ? 'Controllo in corso...' : 'Aggiorna ricevute PEC'}
                                </DropdownMenuItem>
                                {record.ricevuta_accettazione && (
                                  <DropdownMenuItem onClick={() => downloadRicevuta(record, 'accettazione')}>
                                    <FileText className="w-4 h-4 mr-2" />Ricevuta Accettazione
                                  </DropdownMenuItem>
                                )}
                                {record.ricevuta_consegna && (
                                  <DropdownMenuItem onClick={() => downloadRicevuta(record, 'consegna')}>
                                    <FileText className="w-4 h-4 mr-2" />Ricevuta Consegna
                                  </DropdownMenuItem>
                                )}
                                {record.ricevuta_mancata_consegna && (
                                  <DropdownMenuItem onClick={() => downloadRicevuta(record, 'mancata_consegna')} className="text-red-600">
                                    <FileText className="w-4 h-4 mr-2" />Avviso Mancata Consegna
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(record.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <p className="text-sm text-slate-500">
                Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} record
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Precedente
                </Button>
                <span className="text-sm text-slate-600 px-2">
                  Pagina {currentPage} di {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Record Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuova Richiesta Esenzione Canone RAI</DialogTitle>
            <DialogDescription>
              Seleziona il cliente e l'anno per la richiesta di esenzione
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Client Search */}
            <div className="space-y-2 relative">
              <div className="flex items-center justify-between">
                <Label>Cliente *</Label>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs gap-1" onClick={openNewClient}>
                  <UserPlus className="w-3.5 h-3.5" />
                  Nuovo cliente
                </Button>
              </div>
              <Input
                placeholder="Cerca cliente per nome o codice fiscale..."
                value={clientSearchTerm}
                onChange={(e) => handleClientSearch(e.target.value)}
                onFocus={() => setShowClientDropdown(true)}
                onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                data-testid="client-search-input"
              />

              {/* Client dropdown — appare solo mentre si cerca, non occupa spazio fisso */}
              {showClientDropdown && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                  {filteredClients.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">
                      Nessun cliente trovato
                    </div>
                  ) : (
                    filteredClients.slice(0, 10).map((client) => (
                      <div
                        key={client.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedClientId(client.id);
                          setClientSearchTerm(`${client.last_name} ${client.first_name}`);
                          setShowClientDropdown(false);
                        }}
                        className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${
                          selectedClientId === client.id ? 'bg-blue-50 border-blue-200' : ''
                        }`}
                        data-testid={`client-option-${client.id}`}
                      >
                        <div className="font-medium">{client.last_name} {client.first_name}</div>
                        <div className="text-sm text-slate-500 font-mono">{client.fiscal_code}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {selectedClientId && (
                <p className="text-sm text-green-600">
                  Cliente selezionato
                </p>
              )}
              <ClientCompletionBanner
                client={clients.find(c => c.id === selectedClientId)}
                onSaved={(updated) => setClients(prev => prev.map(c => c.id === updated.id ? updated : c))}
              />
            </div>

            {/* Year Selection */}
            <div className="space-y-2">
              <Label>Anno Esenzione *</Label>
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Documento cliente, per la Delega trasmissione Agenzia delle Entrate */}
            <div className="space-y-2 border-t pt-3">
              <Label className="text-sm">Documento del cliente (per la Delega)</Label>
              <p className="text-xs text-slate-500 -mt-1">Si compila da solo caricando un documento qui sotto (viene salvato anche in anagrafica), oppure inseriscilo a mano.</p>
              <div className="grid grid-cols-2 gap-2">
                <Select value={docTipo} onValueChange={setDocTipo}>
                  <SelectTrigger><SelectValue placeholder="Tipo documento" /></SelectTrigger>
                  <SelectContent>
                    {NEW_CLIENT_DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Numero documento" value={docNumero} onChange={(e) => setDocNumero(e.target.value.toUpperCase())} />
              </div>
              <Input placeholder="Rilasciato da (facoltativo)" value={docEnte} onChange={(e) => setDocEnte(e.target.value.toUpperCase())} />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-xs"
                disabled={!selectedClientId || !docTipo || extractingMainDoc}
                onClick={() => mainDocInputRef.current?.click()}
              >
                {extractingMainDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Estrai da documento e salva in anagrafica
              </Button>
              <input
                ref={mainDocInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  handleExtractMainDoc(f);
                }}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Note aggiuntive..."
                rows={2}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                onClick={handleCreate}
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!selectedClientId}
                data-testid="create-canone-rai-btn"
              >
                Crea Richiesta
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== NUOVO CLIENTE DIALOG (estrazione OCR documento) ========== */}
      <Dialog open={newClientDialogOpen} onOpenChange={setNewClientDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo cliente</DialogTitle>
            <DialogDescription>Inserisci solo i dati essenziali. Potrai completare l'anagrafica in seguito.</DialogDescription>
          </DialogHeader>

          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
              <Sparkles className="w-4 h-4" />
              Estrazione automatica dati (facoltativa)
            </div>
            <p className="text-xs text-violet-700">
              Carica il documento d'identità: i campi sotto si compilano da soli (puoi comunque correggerli a mano).
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs text-violet-700">Tipo documento che stai per caricare</Label>
              <Select value={newClientDocTipo} onValueChange={setNewClientDocTipo}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NEW_CLIENT_DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingNewClientDoc ? 'opacity-60 pointer-events-none' : ''}`}>
              {extractingNewClientDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Carica {newClientDocTipo}
              <input
                type="file"
                accept="application/pdf,image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  handleExtractNewClientDoc(f);
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cognome *</Label>
              <Input value={newClient.last_name} onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={newClient.first_name} onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs">Sesso</Label>
              <Select value={newClient.gender} onValueChange={(v) => setNewClient({ ...newClient, gender: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Maschio</SelectItem>
                  <SelectItem value="F">Femmina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Data di nascita</Label>
              <DateInput value={newClient.birth_date} onChange={(val) => setNewClient({ ...newClient, birth_date: val })} placeholder="gg/mm/aaaa" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="canone-rai-birth-abroad" checked={newClient.birth_abroad}
                        onCheckedChange={(v) => setNewClient({ ...newClient, birth_abroad: !!v, birth_comune: '', birth_provincia: '', birth_country: '' })} />
              <Label htmlFor="canone-rai-birth-abroad" className="text-sm">Nato/a all'estero</Label>
            </div>
            {newClient.birth_abroad ? (
              <div className="col-span-2">
                <Label className="text-xs">Nazione di nascita</Label>
                <Input value={newClient.birth_country} onChange={(e) => setNewClient({ ...newClient, birth_country: e.target.value.toUpperCase() })} />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Comune di nascita</Label>
                  <Input value={newClient.birth_comune} onChange={(e) => setNewClient({ ...newClient, birth_comune: e.target.value.toUpperCase() })} />
                </div>
                <div>
                  <Label className="text-xs">Provincia (sigla)</Label>
                  <Input maxLength={2} value={newClient.birth_provincia}
                         onChange={(e) => setNewClient({ ...newClient, birth_provincia: e.target.value.toUpperCase() })} />
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label className="text-xs">Codice fiscale</Label>
              <div className="flex gap-2">
                <Input value={newClient.fiscal_code}
                       onChange={(e) => setNewClient({ ...newClient, fiscal_code: e.target.value.toUpperCase() })} />
                <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfDaDati}>Calcola CF</Button>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Indirizzo residenza <span className="text-blue-600">(ricerca Google Maps)</span></Label>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-2">
                  {GOOGLE_MAPS_API_KEY ? (
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <AddressAutocomplete
                        value={newClient.address}
                        defaultValue={newClient.address}
                        placeholder="es. Via Roma, Piazza Duomo..."
                        onPlaceSelect={(d) => setNewClient(prev => ({
                          ...prev,
                          address: (d.address || prev.address || '').toUpperCase(),
                          address_number: (d.address_number || prev.address_number || '').toUpperCase(),
                          city: (d.city || prev.city || '').toUpperCase(),
                          city_provincia: (d.city_provincia || prev.city_provincia || '').toUpperCase(),
                          postal_code: d.postal_code || prev.postal_code,
                        }))}
                      />
                    </APIProvider>
                  ) : (
                    <Input placeholder="Via/Piazza..." value={newClient.address}
                           onChange={(e) => setNewClient({ ...newClient, address: e.target.value.toUpperCase() })} />
                  )}
                </div>
                <Input placeholder="Civico" value={newClient.address_number}
                       onChange={(e) => setNewClient({ ...newClient, address_number: e.target.value.toUpperCase() })} />
                <Input placeholder="CAP" value={newClient.postal_code}
                       onChange={(e) => setNewClient({ ...newClient, postal_code: e.target.value })} />
                <Input className="col-span-2" placeholder="Città" value={newClient.city}
                       onChange={(e) => setNewClient({ ...newClient, city: e.target.value.toUpperCase() })} />
                <Input placeholder="Prov (sigla)" maxLength={2} value={newClient.city_provincia}
                       onChange={(e) => setNewClient({ ...newClient, city_provincia: e.target.value.toUpperCase() })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Telefono</Label>
              <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setNewClientDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveNewClient} disabled={creatingClient}>
              {creatingClient && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crea e seleziona
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conferma invio PEC */}
      <Dialog open={!!sendPecConfirm} onOpenChange={(open) => { if (!open) setSendPecConfirm(null); }}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-600" />
              Invia PEC
            </DialogTitle>
            <DialogDescription>
              Stai per inviare la richiesta di esenzione Canone RAI
              {sendPecConfirm ? ` per ${sendPecConfirm.client_name || 'il cliente selezionato'}` : ''}.
              Controlla qui sotto il modulo che verrà allegato (dati e firma) prima di inviare.
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg bg-slate-100 h-[60vh] flex items-center justify-center overflow-hidden">
            {pecPreview.loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : pecPreview.url ? (
              <iframe title="Anteprima modulo da inviare" src={pecPreview.url} className="w-full h-full bg-white" />
            ) : (
              <p className="text-sm text-slate-500">{pecPreview.error || 'Anteprima non disponibile'}</p>
            )}
          </div>
          <div className="flex items-center gap-2 py-2">
            <Checkbox
              id="send-copy-to-client"
              checked={sendCopyToClient}
              onCheckedChange={(v) => setSendCopyToClient(!!v)}
            />
            <Label htmlFor="send-copy-to-client" className="cursor-pointer font-normal">
              Invia anche una copia email al cliente (con la richiesta in allegato)
            </Label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSendPecConfirm(null)}>
              Annulla
            </Button>
            <Button
              onClick={() => {
                const record = sendPecConfirm;
                setSendPecConfirm(null);
                handleSendPec(record, sendCopyToClient);
              }}
              disabled={sendingPec === sendPecConfirm?.id}
            >
              <Send className="w-4 h-4 mr-2" />
              Invia PEC
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PEC Configuration Dialog */}
      <Dialog open={pecDialogOpen} onOpenChange={setPecDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-violet-600" />
              Configurazione PEC Canone RAI
            </DialogTitle>
            <DialogDescription>
              Imposta le credenziali della casella PEC che invierà le richieste di esenzione. Funziona con Aruba PEC, Namirial, Postecert, ecc.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            {/* Stato attuale */}
            {pecConfigLoaded && (
              <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${pecConfigLoaded.configured ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                {pecConfigLoaded.configured ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                <span>
                  {pecConfigLoaded.configured
                    ? `PEC configurata${pecConfigLoaded.updated_at ? ` - ultimo aggiornamento: ${new Date(pecConfigLoaded.updated_at).toLocaleString('it-IT')}` : ''}`
                    : 'PEC non ancora configurata'}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Indirizzo PEC *</Label>
                <Input
                  type="email"
                  placeholder="esempio@namirialpec.it"
                  value={pecConfig.pec_email}
                  onChange={(e) => setPecConfig(c => ({ ...c, pec_email: e.target.value }))}
                  data-testid="pec-email-input"
                  className="mt-1"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-medium">
                  Password PEC {pecConfigLoaded?.password_set ? '(lascia vuoto per non cambiare)' : '*'}
                </Label>
                <Input
                  type="password"
                  placeholder={pecConfigLoaded?.password_set ? '••••••••' : 'Password della casella PEC'}
                  value={pecConfig.pec_password}
                  onChange={(e) => setPecConfig(c => ({ ...c, pec_password: e.target.value }))}
                  data-testid="pec-password-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Server SMTP *</Label>
                <Input
                  placeholder="es. smtps.pec.aruba.it"
                  value={pecConfig.smtp_server}
                  onChange={(e) => setPecConfig(c => ({ ...c, smtp_server: e.target.value }))}
                  data-testid="pec-smtp-server-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Porta SMTP *</Label>
                <Select value={String(pecConfig.smtp_port)} onValueChange={(v) => setPecConfig(c => ({ ...c, smtp_port: parseInt(v) }))}>
                  <SelectTrigger className="mt-1" data-testid="pec-smtp-port-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="465">465 (SSL/TLS - consigliato)</SelectItem>
                    <SelectItem value="587">587 (STARTTLS)</SelectItem>
                    <SelectItem value="25">25 (non sicuro)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium">Server IMAP</Label>
                <Input
                  placeholder="se vuoto, usa lo stesso del Server SMTP"
                  value={pecConfig.imap_server}
                  onChange={(e) => setPecConfig(c => ({ ...c, imap_server: e.target.value }))}
                  data-testid="pec-imap-server-input"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Serve SOLO per scaricare le ricevute PEC (accettazione + consegna). Stessa password dell'invio.</p>
              </div>

              <div>
                <Label className="text-sm font-medium">Porta IMAP</Label>
                <Input
                  type="number"
                  value={pecConfig.imap_port}
                  onChange={(e) => setPecConfig(c => ({ ...c, imap_port: parseInt(e.target.value) || 993 }))}
                  data-testid="pec-imap-port-input"
                  className="mt-1"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Destinatario Default Canone RAI</Label>
                <Input
                  type="email"
                  value={pecConfig.default_recipient}
                  onChange={(e) => setPecConfig(c => ({ ...c, default_recipient: e.target.value }))}
                  data-testid="pec-default-recipient-input"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Email RAI dove vengono inviate le richieste di esenzione</p>
              </div>
            </div>

            {/* Esempi server PEC */}
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="font-medium mb-1.5">Server PEC comuni:</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 font-mono">
                <div>• <strong>Aruba</strong>: smtps.pec.aruba.it:465</div>
                <div>• <strong>Namirial</strong>: pro-smtps.sicurezzapostale.it:465</div>
                <div>• <strong>Postecert</strong>: smtps.poste.it:465</div>
                <div>• <strong>Legalmail</strong>: sendm.cert.legalmail.it:465</div>
              </div>
            </div>

            {/* Test PEC */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Send className="w-4 h-4 text-blue-600" />
                Test invio PEC
              </Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email-destinatario-di-test@esempio.it"
                  value={pecTestRecipient}
                  onChange={(e) => setPecTestRecipient(e.target.value)}
                  data-testid="pec-test-recipient-input"
                />
                <Button
                  onClick={testPecConfig}
                  disabled={pecTesting || !pecTestRecipient}
                  variant="outline"
                  data-testid="pec-test-btn"
                >
                  {pecTesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Invia Test
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Invia una mail di prova per verificare che la configurazione funzioni. Puoi usare la stessa email PEC come destinatario.
              </p>

              {pecTestResult && (
                <div className={`mt-3 p-3 rounded-lg text-sm flex items-start gap-2 ${pecTestResult.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                  {pecTestResult.ok ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <div>{pecTestResult.message}</div>
                </div>
              )}
            </div>

            {/* Pulsanti azione */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setPecDialogOpen(false)}>Chiudi</Button>
              <Button 
                onClick={savePecConfig}
                disabled={pecSaving}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="pec-save-btn"
              >
                {pecSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Salva Configurazione
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale anteprima PDF ricevuta (iframe, stessa pagina) */}
      {delegaGenerale && <FinestraDelegaGenerale clientId={delegaGenerale.client_id} clientName={delegaGenerale.client_name}
        onChiudi={() => { setDelegaGenerale(null); fetchRecords(); }} onFirmata={() => dopoDelegaFirmata(delegaGenerale)} />}
      <Dialog open={previewDialog.open} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="preview-dialog">
          <DialogHeader className="px-4 pt-4 pb-2 border-b flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="truncate" title={previewDialog.title}>
                {previewDialog.title}
              </DialogTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const iframe = document.getElementById('canone-rai-ricevuta-preview-iframe');
                    if (iframe && iframe.contentWindow) {
                      try { iframe.contentWindow.print(); } catch (_e) { toast.error('Impossibile aprire il dialog di stampa'); }
                    }
                  }}
                  data-testid="preview-print-btn"
                >
                  <FileText className="w-4 h-4 mr-1" /> Stampa
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (!previewDialog.url) return;
                    const a = document.createElement('a');
                    a.href = previewDialog.url;
                    a.download = `${previewDialog.title.replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                  }}
                  data-testid="preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-1" /> Scarica
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100">
            {previewDialog.url && (
              <iframe
                id="canone-rai-ricevuta-preview-iframe"
                title="Anteprima ricevuta"
                src={previewDialog.url}
                className="w-full h-full border-0"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Input nascosto per l'upload della Delega scansionata — triggerato da
          triggerUploadDelegaFirmata, un solo elemento condiviso da tutte le righe. */}
      <input
        type="file"
        ref={delegaFirmataInputRef}
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          handleUploadDelegaFirmata(f);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default CanoneRai;
